// Shopify renders product truth, prices and translated result markup.
export function searchQuery(query, category = '') {
  const words = query.trim();
  const tokens = words.toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) || [];
  const categoryTokens = category.toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) || [];
  return category && !categoryTokens.every(token => tokens.includes(token)) ? `${words} ${category}`.trim() : words;
}

export function searchURL(endpoint, query, category, origin, predictive = false) {
  const url = new URL(endpoint, origin);
  if (url.origin !== origin) throw new Error('Search must remain on the current storefront');
  url.searchParams.set('q', searchQuery(query, category));
  if (predictive) {
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title');
    url.searchParams.set('section_id', 'sf-menu-search-results');
    // The storefront preview cookie already selects the duplicate. Adding
    // preview_theme_id here redirects and corrupts nested resources parameters.
  } else {
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
  }
  return url.href;
}

export class LatestSearch {
  constructor(fetcher = (url, options) => fetch(url, options), timeout = 10000) { this.fetcher = fetcher; this.timeout = timeout; this.version = 0; }
  cancel() { this.version++; this.controller?.abort(); }
  async run(url) {
    this.cancel();
    const version = this.version;
    const controller = this.controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeout);
    try {
      const response = await this.fetcher(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Search unavailable');
      const html = await response.text();
      return version === this.version ? html : null;
    } catch (error) {
      if (version !== this.version) return null;
      throw error;
    } finally { clearTimeout(timer); }
  }
}

export function selectSuggestions(rows, category, limit = 6) {
  return rows.filter(row => !category || row.categories.includes(category)).slice(0, limit);
}

if (globalThis.customElements && !customElements.get('sf-menu-search')) {
  customElements.define('sf-menu-search', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const on = (node, type, callback, options = {}) => node?.addEventListener(type, callback, { ...options, signal: this.events.signal });
      this.input = this.querySelector('[data-input]');
      this.panel = this.querySelector('[data-panel]');
      this.nav = this.querySelector('[data-navigation]');
      this.intro = this.querySelector('[data-intro]');
      this.results = this.querySelector('[data-results]');
      this.facets = this.querySelector('[data-facets]');
      this.notice = this.querySelector('[data-notice]');
      this.live = this.querySelector('[data-live]');
      this.clear = this.querySelector('[data-clear]');
      this.cancelButton = this.querySelector('[data-cancel]');
      this.retry = this.querySelector('[data-retry]');
      this.all = this.querySelector('[data-all]');
      this.transport = new LatestSearch();
      this.category = '';
      this.active = false;
      this.drawer = this.closest('header-drawer');
      this.modal = this.closest('dialog-component');
      // Native containment remains in charge; refresh Tab order from currently
      // visible controls because result/intro/navigation nodes change in place.
      on(window, 'keydown', event => {
        if (event.key !== 'Tab' || !this.drawer?.querySelector('[ref="details"]')?.open || !this.drawer.contains(document.activeElement)) return;
        const controls = [...this.drawer.querySelectorAll('summary, a[href], button:enabled, input:not([type="hidden"]):enabled, select:enabled, textarea:enabled, [tabindex]')]
          .filter(element => element.tabIndex >= 0 && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden' && !element.closest('[inert]'));
        if (!controls.length) return;
        event.preventDefault(); event.stopImmediatePropagation();
        const current = controls.indexOf(document.activeElement);
        controls[(current + (event.shiftKey ? -1 : 1) + controls.length) % controls.length].focus();
      }, { capture: true });
      on(this.input, 'focus', () => { if (!this.suppressFocus) this.begin(); });
      on(this.input, 'pointerdown', () => this.begin());
      on(this.input, 'input', () => {
        this.begin(); this.category = ''; this.transport.cancel(); clearTimeout(this.timer);
        this.results.replaceChildren(); this.facets.hidden = true; this.notice.textContent = ''; this.all.hidden = true;
        this.clear.hidden = !this.input.value; this.scrollTop = 0;
        this.timer = setTimeout(() => this.render(), 180);
      });
      on(this.clear, 'click', () => { this.input.value = ''; this.category = ''; this.input.focus(); this.render(); });
      on(this.cancelButton, 'click', () => this.leave(true));
      on(this.retry, 'click', async () => { await this.render(); if (this.active) this.input.focus(); });
      on(this.intro, 'click', event => {
        const button = event.target.closest('[data-term]');
        if (!button) return;
        this.input.value = button.dataset.term; this.category = ''; this.input.focus(); this.render();
      });
      on(this.facets, 'click', event => {
        const button = event.target.closest('[data-category]');
        if (!button) return;
        this.category = button.dataset.category; this.render();
      });
      on(this.querySelector('form'), 'submit', event => {
        if (this.category) {
          event.preventDefault();
          window.location.assign(this.url(false));
        }
      });
      on(this, 'keydown', event => {
        const links = [...this.results.querySelectorAll('.search-product')];
        if (!['ArrowUp', 'ArrowDown'].includes(event.key) || !links.length) return;
        const index = links.indexOf(document.activeElement);
        if (document.activeElement === this.input && event.key === 'ArrowDown') { event.preventDefault(); links[0].focus(); }
        else if (index >= 0) {
          event.preventDefault(); const next = index + (event.key === 'ArrowDown' ? 1 : -1);
          if (next < 0) this.input.focus(); else links[Math.min(next, links.length - 1)].focus();
        }
      });
      // Horizon handles Escape on keyup and targets the nearest <details>.
      // Search owns its first Escape; nested disclosures must not close the host.
      on(this.drawer, 'keyup', event => {
        if (event.key !== 'Escape') return;
        if (this.active) { event.stopImmediatePropagation(); event.preventDefault(); this.leave(true); }
        else {
          const disclosure = event.target.closest('details');
          if (disclosure && disclosure !== this.drawer.querySelector('[ref="details"]')) {
            event.stopImmediatePropagation(); disclosure.open = false; disclosure.querySelector('summary')?.focus();
          }
        }
      }, { capture: true });
      on(this.drawer?.querySelector('[ref="details"]'), 'toggle', event => { if (!event.target.open) this.leave(); });
      on(this.modal?.querySelector('dialog'), 'close', () => this.leave());
      on(this.modal, 'dialog:open', () => { this.begin(); this.input.focus(); });
      if (this.dataset.mode === 'standalone') this.begin();
      this.dataset.ready = 'true';
    }

    url(predictive) {
      const term = [...this.facets.querySelectorAll('[data-category]')].find(button => button.dataset.category === this.category)?.dataset.queryTerm || '';
      return searchURL(predictive ? this.dataset.suggestUrl : this.dataset.searchUrl, this.input.value, term, location.origin, predictive);
    }
    begin() {
      if (this.active) return;
      this.active = true; this.classList.add('search-active'); this.panel.hidden = false;
      if (this.nav) this.nav.hidden = true;
      this.cancelButton.hidden = !this.nav;
      this.render();
    }
    leave(restore = false) {
      clearTimeout(this.timer); this.transport.cancel(); this.active = false; this.category = ''; this.input.value = '';
      this.classList.remove('search-active'); this.panel.hidden = true; this.clear.hidden = true; this.cancelButton.hidden = true;
      this.results.replaceChildren(); this.live.textContent = ''; this.panel.setAttribute('aria-busy', 'false');
      if (this.nav) this.nav.hidden = false;
      if (restore) { this.suppressFocus = true; this.input.focus(); this.suppressFocus = false; }
    }
    async render() {
      clearTimeout(this.timer); this.transport.cancel();
      if (!this.active) return;
      const query = this.input.value.trim();
      this.clear.hidden = !query; this.intro.hidden = !!query; this.intro.classList.remove('search-recovery');
      this.results.replaceChildren(); this.notice.textContent = ''; this.retry.hidden = true;
      this.all.hidden = !query; this.all.href = this.url(false); this.facets.hidden = !query;
      this.facets.querySelectorAll('[data-category]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.category === this.category)));
      this.panel.setAttribute('aria-busy', 'false');
      if (!query) { this.live.textContent = ''; return; }
      this.panel.setAttribute('aria-busy', 'true'); this.notice.textContent = this.dataset.loading; this.live.textContent = this.dataset.loading;
      try {
        const html = await this.transport.run(this.url(true));
        if (html === null || !this.active) return;
        const response = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sf-search-response]');
        if (!response) throw new Error('Missing search section');
        const rows = [...response.querySelectorAll('.search-result')].map(node => ({node, categories: (node.dataset.categories || '').split('|')}));
        const selected = selectSuggestions(rows, this.category);
        const keep = new Set(selected.map(row => row.node));
        rows.forEach(row => { if (!keep.has(row.node)) row.node.remove(); });
        const count = selected.length;
        const message = response.querySelector(`[data-count-message="${count}"]`).content.textContent;
        response.querySelector('.search-heading h3').textContent = message;
        if (this.category && count === 0) response.querySelector('.search-notice')?.remove();
        this.results.replaceChildren(...response.childNodes);
        this.notice.textContent = count === 0 && this.category ? response.dataset.categoryEmpty : '';
        this.live.textContent = message;
        if (count === 0) { this.intro.hidden = false; this.intro.classList.add('search-recovery'); }
        this.panel.setAttribute('aria-busy', 'false');
      } catch {
        if (!this.active) return;
        this.panel.setAttribute('aria-busy', 'false'); this.notice.textContent = this.dataset.error; this.live.textContent = this.dataset.error; this.retry.hidden = false;
      }
    }
    disconnectedCallback() { clearTimeout(this.timer); this.transport?.cancel(); this.events?.abort(); this.events = null; }
  });
}
